import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Table, Row, Col, Card, CardBody, Input, Badge, Pagination, PaginationItem, PaginationLink, Label } from "reactstrap";
import { useDispatch } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import Select from "react-select";
import dayjs from 'dayjs';

const StaffList = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [staffs, setStaffs] = useState([]);
    const [summary, setSummary] = useState({
        total_staff: 0,
        active_staff: 0,
        inactive_staff: 0
    });
    const [pagination, setPagination] = useState({ current_page: 1, total_pages: 1, total_records: 0 });

    const [filters, setFilters] = useState({
        name: "",
        email: "",
        phone: "",
        status: null,
        page: 1,
        limit: 10
    });

    const statusOptions = [
        { value: "1", label: "Active" },
        { value: "0", label: "Inactive" }
    ];

    const customSelectStyles = {
        control: (provided) => ({
            ...provided,
            minHeight: '45px',
            borderRadius: '8px',
            borderColor: '#dee2e6',
            boxShadow: 'none',
            '&:hover': { borderColor: '#2e4a3d' }
        }),
    };

    const loadStaffs = async () => {
        dispatch(loadingTrue());
        try {
            const params = {
                ...filters,
                status: filters.status ? filters.status.value : "",
            };
            const queryParams = new URLSearchParams(params).toString();
            const res = await Handler({ method: "GET", url: `/staff/list.php?${queryParams}` });

            if (res?.success) {
                setStaffs(res.data || []);
                setSummary(res.summary); // 🔥 API Summary Stats
                setPagination({
                    current_page: res?.current_page || 1,
                    total_pages: res?.total_pages || 1,
                    total_records: res?.total_records || 0
                });
            } else {
                toast.error(res?.message || "Failed to fetch data");
            }
        } catch (err) {
            toast.error("Server connection error");
        } finally {
            dispatch(loadingFalse());
        }
    };

    const handleStatusToggle = async (id, currentStatus) => {
        const newStatus = currentStatus === 1 ? 0 : 1;
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: `Do you want to ${newStatus === 1 ? 'Activate' : 'Deactivate'} this staff?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#2e4a3d',
            confirmButtonText: 'Yes, update it!'
        });

        if (result.isConfirmed) {
            dispatch(loadingTrue());
            try {
                const res = await Handler({
                    method: "POST",
                    url: "/staff/update.php",
                    data: { id: id, status: newStatus }
                });

                if (res?.success) {
                    toast.success(`Staff status updated!`);
                    loadStaffs();
                } else {
                    toast.error(res?.message || "Update failed");
                }
            } catch (e) {
                toast.error("Error updating status");
            } finally {
                dispatch(loadingFalse());
            }
        }
    };

    useEffect(() => { loadStaffs(); }, [filters.page, filters.status]);

    const handleFilterChange = (e) => {
        setFilters({ ...filters, [e.target.name]: e.target.value, page: 1 });
    };

    const handleSearch = (e) => { if (e.key === 'Enter') loadStaffs(); };

    return (
        <div className="page-container p-1 bg-light min-vh-100">
            <div className="page-header mb-4">
                <h6 className="text-muted small">Management &gt; Staffs</h6>
                <div className="d-flex justify-content-between align-items-center">
                    <div>
                        <h3 className="fw-bold text-dark m-0">Staff Directory</h3>
                        <p className="text-muted small">
                            Total: <b>{summary.total_staff}</b> | 
                            Active: <span className="text-success">{summary.active_staff}</span> | 
                            Inactive: <span className="text-danger">{summary.inactive_staff}</span>
                        </p>
                    </div>
                    <Button color="danger" className="px-4 shadow-sm fw-bold" onClick={() => navigate("/staffs/add")}>
                        <i className="bx bx-plus me-1"></i> Add New Staff
                    </Button>
                </div>
            </div>

            <Card className="border-0 shadow-sm mb-4 rounded-4">
                <CardBody className="p-3">
                    <Row className="g-2 align-items-end">
                        <Col md={3}>
                            <Label className="small fw-bold">Name</Label>
                            <Input style={{ height: '45px' }} name="name" placeholder="Search name..." value={filters.name} onChange={handleFilterChange} onKeyDown={handleSearch} />
                        </Col>
                        <Col md={3}>
                            <Label className="small fw-bold">Email</Label>
                            <Input style={{ height: '45px' }} name="email" placeholder="Email address..." value={filters.email} onChange={handleFilterChange} onKeyDown={handleSearch} />
                        </Col>
                        <Col md={2}>
                            <Label className="small fw-bold">Phone</Label>
                            <Input style={{ height: '45px' }} name="phone" placeholder="Phone..." value={filters.phone} onChange={handleFilterChange} onKeyDown={handleSearch} />
                        </Col>
                        <Col md={2}>
                            <Label className="small fw-bold">Status</Label>
                            <Select
                                options={statusOptions}
                                value={filters.status}
                                onChange={(opt) => setFilters({ ...filters, status: opt, page: 1 })}
                                styles={customSelectStyles}
                                isClearable
                                placeholder="All"
                            />
                        </Col>
                        <Col md={2}>
                            <Button color="primary" block style={{ height: '45px', background: '#2e4a3d', border: 'none' }} onClick={loadStaffs}>
                                <i className="bx bx-search-alt me-1"></i> Search
                            </Button>
                        </Col>
                    </Row>
                </CardBody>
            </Card>

            <Card className="border-0 shadow-sm rounded-4">
                <CardBody className="p-0">
                    <Table responsive hover className="mb-0 align-middle">
                        <thead className="bg-light text-muted small text-uppercase">
                            <tr>
                                <th className="ps-4">S.No</th>
                                <th>Staff Details</th>
                                <th>Contact Info</th>
                                <th>Status</th>
                                <th>Joined Date</th>
                                <th className="text-center">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {staffs.length > 0 ? (
                                staffs.map((item, index) => (
                                    <tr key={item.id}>
                                        <td className="ps-4">{(pagination.current_page - 1) * filters.limit + index + 1}</td>
                                        <td>
                                            <div className="fw-bold text-dark">{item.staff.name?.toUpperCase()}</div>
                                            <Badge color="soft-info" className="text-info text-uppercase" style={{fontSize: '10px'}}>{item.role.label}</Badge>
                                        </td>
                                        <td>
                                            <div className="text-lowercase small"><i className="bx bx-envelope me-1 text-muted"></i>{item.staff.email}</div>
                                            <div className="small"><i className="bx bx-phone me-1 text-muted"></i>{item.staff.phone}</div>
                                        </td>
                                        <td>
                                            <div className="form-check form-switch d-flex align-items-center">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    checked={item.status.value === 1}
                                                    onChange={() => handleStatusToggle(item.id, item.status.value)}
                                                    style={{ cursor: 'pointer' }}
                                                />
                                                <Badge color={item.status.value === 1 ? "success" : "danger"} pill className="ms-2">
                                                    {item.status.label}
                                                </Badge>
                                            </div>
                                        </td>
                                        <td>{dayjs(item.created_at).format('DD-MM-YYYY')}</td>
                                        <td className="text-center">
                                            <Button color="light" size="sm" className="rounded-3 shadow-sm border" onClick={() => navigate(`/staffs/${item.id}`)}>
                                                <i className="bx bx-edit text-primary fs-5"></i>
                                            </Button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr><td colSpan="6" className="text-center py-5 text-muted italic">No staff records found.</td></tr>
                            )}
                        </tbody>
                    </Table>
                </CardBody>
            </Card>

            {pagination?.total_pages > 1 && (
                <div className="d-flex justify-content-center mt-4">
                    <Pagination className="pagination-rounded shadow-sm">
                        <PaginationItem disabled={filters.page <= 1}>
                            <PaginationLink previous onClick={() => setFilters({ ...filters, page: filters.page - 1 })} />
                        </PaginationItem>
                        {[...Array(pagination?.total_pages)].map((_, i) => (
                            <PaginationItem active={i + 1 === filters.page} key={i}>
                                <PaginationLink onClick={() => setFilters({ ...filters, page: i + 1 })}>{i + 1}</PaginationLink>
                            </PaginationItem>
                        ))}
                        <PaginationItem disabled={filters.page >= pagination?.total_pages}>
                            <PaginationLink next onClick={() => setFilters({ ...filters, page: filters.page + 1 })} />
                        </PaginationItem>
                    </Pagination>
                </div>
            )}
        </div>
    );
};

export default StaffList;